import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { RightOutlined } from "@ant-design/icons";
import { FileText } from "lucide-react";

import BaseTag from "@/components_v2/Tags/BaseTag";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import { DataFileResponse } from "@/redux/files/types";
import { BaseUserResponse } from "@/redux/users/types";
import {
  DiffVariant,
  ResolvedItem,
  SelectComponentProps,
  TextDisplayProps,
  ValueDiffProps,
} from "../../types";
import { getRelatedDataItemDisplayLabel } from "./utils";

import "../../styles.css";

const variantClass = (variant: DiffVariant): string =>
  variant === DiffVariant.OLD
    ? "history-diff-variant--removed"
    : "history-diff-variant--added";

export function ValueDiff<T extends string | ResolvedItem>({
  oldItems,
  newItems,
  Component,
  showArrow = false,
}: ValueDiffProps<T>) {
  const hasOld = Boolean(oldItems && oldItems.length > 0);
  const hasNew = Boolean(newItems && newItems.length > 0);

  const diffItemKey = (
    item: string | ResolvedItem,
    i: number,
    prefix: string,
  ): string =>
    item && typeof item === "object" && "id" in item
      ? `${prefix}-${item.id}-${i}`
      : `${prefix}-${String(item)}-${i}`;

  return (
    <div className="history-value-diff">
      <div className="history-value-diff__content flex flex-wrap items-center gap-150">
        {oldItems?.map((oldItem, i) => (
          <div
            key={diffItemKey(oldItem, i, "old")}
            className="history-value-diff__item"
          >
            <Component
              value={oldItem}
              className={variantClass(DiffVariant.OLD)}
            />
          </div>
        ))}

        {showArrow && hasOld && hasNew && (
          <RightOutlined className="history-value-diff__arrow flex-shrink-0" />
        )}

        {newItems?.map((newItem, i) => (
          <div
            key={diffItemKey(newItem, i, "new")}
            className="history-value-diff__item"
          >
            <Component
              value={newItem}
              className={variantClass(DiffVariant.NEW)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export const TextDisplay = ({ value, className }: TextDisplayProps) => {
  return (
    <span
      className={`history-value-chip body-s-regular ${className ?? ""}`.trim()}
    >
      {value}
    </span>
  );
};

export const TagDisplay = ({ value, className }: SelectComponentProps) => {
  const label = getRelatedDataItemDisplayLabel(value.item);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const measure = useCallback(() => {
    const el = labelRef.current;
    if (!el || !label) {
      setIsTruncated(false);
      return;
    }
    setIsTruncated(el.scrollWidth > el.clientWidth + 1);
  }, [label]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure]);

  if (!label) {
    return null;
  }

  const tag = (
    <span className="history-tag-display">
      <BaseTag
        hasRoundCorners
        label={
          <span ref={labelRef} className="history-tag-display__label">
            {label}
          </span>
        }
        className={className}
      />
    </span>
  );

  if (isTruncated) {
    return <BaseTooltip title={label}>{tag}</BaseTooltip>;
  }

  return tag;
};

export const UserDisplay = ({ value, className }: SelectComponentProps) => {
  const user = value.item as BaseUserResponse | null;
  return (
    <BaseTag
      hasRoundCorners
      label={user?.fullName ?? ""}
      className={className}
      thumbnail={{
        src: user?.profilePicture,
        alt: user?.fullName ?? "",
      }}
    />
  );
};

export const FileDisplay = ({ value, className }: SelectComponentProps) => {
  const file = value.item as DataFileResponse | null;
  const label = file?.name;
  if (!label) {
    return null;
  }

  return (
    <div
      className={`history-display-file flex items-center gap-200 full-width ${className ?? ""}`.trim()}
    >
      <FileText
        size={16}
        className="history-display-file__icon flex-shrink-0"
      />

      <span className="history-display-file__name body-s-regular">{label}</span>
    </div>
  );
};
